import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { ContactCustomField, ContactDefaultCustomField, User } from 'src/constants/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { Contact, ContactInfoFormData } from 'src/modules/contacts/types';
import { Group } from 'src/modules/groups/types';
import { useContactService } from '../services';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import { buildContactInfoRequestFormData } from '../helpers/builders';
import { ContactMetadataFormData } from '../types/ContactMetadataFormData';
import { formatMetadataValue } from '../helpers/helpers';
import { UseFormReturn } from 'react-hook-form';
import { useContactsApi } from '../api';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';

interface Data {
  contactData: Contact;
  customFields: ContactDefaultCustomField[];
  users: User[];
  groups: Group[];
  contactGroups?: Group[];
}

export interface EditContactDrawerController extends Data {
  isOpen: boolean;
  open: (data: Data) => void;
  close: (isFormDirty?: boolean) => void;
  submitContactInfo: (
    data: ContactInfoFormData,
    form: UseFormReturn<ContactInfoFormData>,
  ) => Promise<void>;
  submitAssignUser: (companyId: string, contactId: string, userId: string) => Promise<void>;
  submitChangeContactGroups: (
    companyId: string,
    contactId: string,
    newGroupIds: string[],
    removedGroupIds: string[],
  ) => Promise<boolean>;
  submitContactMetadata: (
    data: ContactMetadataFormData,
    form: UseFormReturn<ContactMetadataFormData>,
  ) => Promise<void>;
  handleOptOutContact: (contactId: string, companyId: string) => Promise<void>;
  refreshContacts: () => Promise<void>;
}

export const useEditContactDrawerController = (
  refreshAction?: (() => Promise<unknown>) | (() => void),
): EditContactDrawerController => {
  const selectedCompanyId = useSelector(getCurrentCompanyId) as string;
  const {
    updateContactInfo,
    assignUser,
    changeContactGroups,
    changeContactMetadata,
    getContactGroups,
    optOutContact,
  } = useContactService();
  const contactsApi = useContactsApi();

  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);
  const [contactData, setContactData] = useState<Contact | undefined | null>(undefined);
  const [customFields, setCustomFields] = useState<ContactDefaultCustomField[]>([]);
  const [contactGroups, setContactGroups] = useState<Group[]>();
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);

  const refreshContacts = async () => {
    await refreshAction?.();
  };

  const loadContactGroups = useCallback(
    async (contactId: string) => {
      const assignedGroups = await getContactGroups(selectedCompanyId, contactId);
      return assignedGroups.items;
    },
    [getContactGroups, selectedCompanyId],
  );

  const open = useCallback(async (data: Data) => {
    setContactData(data.contactData);
    setCustomFields(data.customFields);
    setGroups(data.groups);
    setUsers(data.users);

    const metadata: ContactCustomField[] = data.customFields.map(c => {
      const field = data.contactData.metadata.find(m => m.metadataId === c.metadataId);

      return {
        contactId: data.contactData.id,
        metadataId: c.metadataId!,
        type: c.type,
        name: c.name,
        description: c.description,
        value: field?.value || '',
        id: field?.id || '',
      };
    });

    setContactData({ ...data.contactData, metadata });

    setIsOpenDrawer(true);

    const assignedGroups = await loadContactGroups(data.contactData.id);
    setContactGroups(assignedGroups);
  }, [loadContactGroups]);

  const close = useCallback((isFormDirty: boolean = false) => {
    if (!isFormDirty) {
      setContactData(undefined);
      setContactGroups(undefined);
      setIsOpenDrawer(false);
    }
  }, []);

  const submitContactInfo = useCallback(
    async (data: ContactInfoFormData, form: UseFormReturn<ContactInfoFormData>) => {
      try {
        // Pre-check existing contact by phone within the company, excluding current contact
        const phone = data.phoneNumber?.toString() || '';
        if (phone) {
          const response = await contactsApi.getContacts({
            companyId: selectedCompanyId,
            phoneNumber: '+1' + phone,
            limit: 1,
            offset: 0,
          });
          const found = response.data.items && response.data.items.length > 0 ? response.data.items[0] : undefined;
          if (found && found.id !== data.id) {
            form.setError('phoneNumber', {
              type: 'manual',
              message: 'A contact with the same phone number already exist within the company.',
            });
            return;
          }
        }

        await updateContactInfo(selectedCompanyId, data.id, buildContactInfoRequestFormData(data));
        await refreshAction?.();
        form.reset(data);
      } catch (e) {
        form.setError('root', {
          type: 'manual',
          message: getApiErrorMessage(e, 'Failed to save contact'),
        });
      }
    },
    [contactsApi, selectedCompanyId, updateContactInfo, refreshAction],
  );

  const submitContactMetadata = useCallback(
    async (data: ContactMetadataFormData, form: UseFormReturn<ContactMetadataFormData>) => {
      try {
        const removedCustomFieldIds = data.customFields
          .filter(c => !c.value && c.id)
          .map(c => c.metadataId);
        const changedCustomFields = data.customFields
          .filter(c => c.value)
          .map(c => ({ metadataId: c.metadataId, value: formatMetadataValue(c.type, c.value) }));
        
        await changeContactMetadata(data.contactId, changedCustomFields, removedCustomFieldIds);
        await refreshAction?.();
        form.reset(data);
      } catch (e) {
        reportErrorToUser(e, getApiErrorMessage(e, GENERIC_ERROR_MESSAGE));
      }
    },
    [changeContactMetadata, refreshAction],
  );

  const submitAssignUser = useCallback(
    async (companyId: string, contactId: string, userId: string) => {
      try {
        await assignUser(companyId, contactId, userId);
        await refreshAction?.();
      } catch (e) {
        reportErrorToUser(e, getApiErrorMessage(e, GENERIC_ERROR_MESSAGE));
      }
    },
    [assignUser, refreshAction],
  );

  const submitChangeContactGroups = useCallback(
    async (
      companyId: string,
      contactId: string,
      newGroupIds: string[],
      removedGroupIds: string[],
    ) => {
      try {
        await changeContactGroups(companyId, contactId, newGroupIds, removedGroupIds);
        const newGroups = groups.filter(g => newGroupIds.includes(g.id));
        const existedGroups = contactGroups?.filter(g => !removedGroupIds.includes(g.id));
        setContactGroups([...(existedGroups || []), ...newGroups]);
        refreshAction?.();

        return true;
      } catch (e) {
        reportErrorToUser(e, getApiErrorMessage(e, GENERIC_ERROR_MESSAGE));

        return false;
      }
    },
    [groups, contactGroups, changeContactGroups, refreshAction],
  );

  const handleOptOutContact = useCallback(async (contactId: string, companyId: string) => {
    try {
      await optOutContact(contactId, companyId);
      refreshAction?.();
    } catch (e) {
      reportErrorToUser(e, getApiErrorMessage(e, GENERIC_ERROR_MESSAGE));
    }
  }, [optOutContact, refreshAction]);

  return {
    isOpen: isOpenDrawer,
    close,
    open,
    contactData: contactData!,
    customFields,
    contactGroups,
    users,
    groups,
    submitContactInfo,
    submitAssignUser,
    submitChangeContactGroups,
    submitContactMetadata,
    handleOptOutContact,
    refreshContacts,
  };
};
